/**
 * Which spend limit, set to $0, is keeping a paused placement paused.
 *
 * $0 is a person's explicit "no ads may run" (owner, 2026-10-04). A placement
 * the spend-sync stopped for a self-resuming reason normally comes back by
 * itself on the next account day or month — but never while the daily ad
 * limit is $0, and never while its own cap is $0 (a $0 cap has no room). The
 * campaign banner and the placement notes say so instead of promising
 * "resumes automatically tomorrow / next month".
 */

import type { AdoptDailyAdLimitDto, AdoptSpendCapsDto } from '@/lib/api';

/**
 * Pause reasons that come from a spend limit: the four self-resuming ones, a
 * lowered daily limit, and an optional cap set to $0 ('spend_cap_zero'). A
 * person setting a limit to $0 relabels the self-resuming ones in its scope to
 * 'daily_limit_lowered' / 'spend_cap_zero', so raising it again brings nothing
 * back by itself.
 */
const SPEND_LIMIT_PAUSE_REASONS = new Set([
  'daily_limit_reached',
  'daily_limit_lowered',
  'monthly_cap_reached',
  'wallet_daily_cap_reached',
  'wallet_monthly_cap_reached',
  'spend_cap_zero',
]);

/**
 * The name of the $0 limit that keeps a placement paused for `pauseReason`
 * ("daily ad limit", "monthly cap", …), or null when none is $0. The daily ad
 * limit at $0 stops everything, so it wins over a cap.
 */
export function zeroSpendLimitName(
  pauseReason: string | null | undefined,
  limit: AdoptDailyAdLimitDto | null | undefined,
  caps: AdoptSpendCapsDto | null | undefined,
): string | null {
  if (!pauseReason || !SPEND_LIMIT_PAUSE_REASONS.has(pauseReason)) return null;
  if (limit?.limitCents === 0) return 'daily ad limit';
  switch (pauseReason) {
    case 'monthly_cap_reached':
      return caps?.monthly.capCents === 0 ? 'monthly cap' : null;
    case 'wallet_daily_cap_reached':
      return caps?.wallet?.dailyCapCents === 0 ? 'wallet daily cap' : null;
    case 'wallet_monthly_cap_reached':
      return caps?.wallet?.monthlyCapCents === 0 ? 'wallet monthly cap' : null;
    case 'spend_cap_zero':
      // The reason does not say which cap froze it; name one that is still $0.
      if (caps?.monthly.capCents === 0) return 'monthly cap';
      if (caps?.wallet?.dailyCapCents === 0) return 'wallet daily cap';
      if (caps?.wallet?.monthlyCapCents === 0) return 'wallet monthly cap';
      return null;
    default:
      return null;
  }
}

/** A placement's note while a $0 limit keeps it paused. */
export function zeroSpendLimitNote(name: string): string {
  return (
    `Paused while the ${name} is $0: no ads can run, and it does not resume by itself. A ` +
    'workspace owner or admin can raise it in Settings; then resume what should run.'
  );
}
